import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MagnifyingGlass,
  SortAscending,
  SortDescending,
  CaretLeft,
  CaretRight,
  CaretDoubleLeft,
  CaretDoubleRight,
  X,
  Plus,
  Minus,
  PaperclipHorizontal,
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export function MaintenanceFleet() {
  const [maintenances, setMaintenances] = useState([]);
  const [filteredMaintenances, setFilteredMaintenances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [sortField, setSortField] = useState('placa');
  const [sortDirection, setSortDirection] = useState('asc');
  const [expandedRows, setExpandedRows] = useState(new Set());

  const [pdfFiles, setPdfFiles] = useState([]);

  const handleRemoveMaintenance = async (id) => {
    try {
      const response = await axios.delete(
        `https://radar.tlpservicos.com.br/api/manutencoes/${id}`
      );

      if (response.status === 200) {
        // Remove o item do estado local
        const updatedMaintenances = maintenances.filter(
          (maintenance) => maintenance.id !== id
        );
        setMaintenances(updatedMaintenances);
        setFilteredMaintenances(
          updatedMaintenances.filter(
            (maintenance) =>
              maintenance.placa.toLowerCase().includes(searchTerm.toLowerCase()) ||
              maintenance.usuario.toLowerCase().includes(searchTerm.toLowerCase())
          )
        );

        toast.success('Manutenção removida com sucesso!');
      }
    } catch (error) {
      console.error('Erro ao remover manutenção:', error);
      toast.error('Erro ao remover a manutenção');
    }
  };

  // Carregar manutenções da API
  useEffect(() => {
    const fetchMaintenances = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          'https://radar.tlpservicos.com.br/api/manutencoes?skip=0&limit=100'
        );
        const data = await response.data;
        if (response.status === 200) {
          setMaintenances(response.data);
          setFilteredMaintenances(response.data);
          setTotalPages(Math.ceil(response.data.length / itemsPerPage));
        }
      } catch (error) {
        console.error('Erro ao buscar manutenções:', error);
        toast.error('Erro ao carregar a lista de manutenções');
      } finally {
        setLoading(false);
      }
    };

    fetchMaintenances();
  }, [itemsPerPage]);

  // Formatar data para exibição
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return format(date, 'dd/MM/yyyy', { locale: ptBR });
    } catch (error) {
      return 'Data inválida';
    }
  };

  // Formatar data e hora para exibição
  const formatDateTime = (dateString) => {
    try {
      const date = new Date(dateString);
      return format(date, 'dd/MM/yyyy HH:mm', { locale: ptBR });
    } catch (error) {
      return 'Data inválida';
    }
  };

  // Filtrar, ordenar e paginar as manutenções
  useEffect(() => {
    let result = [...maintenances];

    // Filtrar
    if (searchTerm) {
      result = result.filter(
        (maintenance) =>
          maintenance.placa.toLowerCase().includes(searchTerm.toLowerCase()) ||
          maintenance.usuario.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Ordenar
    result.sort((a, b) => {
      const fieldA = a[sortField];
      const fieldB = b[sortField];

      if (typeof fieldA === 'string' && typeof fieldB === 'string') {
        return sortDirection === 'asc'
          ? fieldA.localeCompare(fieldB)
          : fieldB.localeCompare(fieldA);
      }

      // Para valores numéricos
      if (fieldA === null) return sortDirection === 'asc' ? -1 : 1;
      if (fieldB === null) return sortDirection === 'asc' ? 1 : -1;

      return sortDirection === 'asc' ? fieldA - fieldB : fieldB - fieldA;
    });

    setFilteredMaintenances(result);
    setTotalPages(Math.ceil(result.length / itemsPerPage));
    setCurrentPage(1); // Resetar para a primeira página após filtrar
  }, [searchTerm, maintenances, sortField, sortDirection, itemsPerPage]);

  // Função para lidar com a classificação da tabela
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Renderizar ícone de ordenação
  const renderSortIcon = (field) => {
    if (sortField !== field) return null;

    return sortDirection === 'asc' ? (
      <SortAscending size={16} className="ml-1" />
    ) : (
      <SortDescending size={16} className="ml-1" />
    );
  };

  // Calcular manutenções para a página atual
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredMaintenances.slice(indexOfFirstItem, indexOfLastItem);

  // Função para mudar de página
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Função para ir para a primeira página
  const goToFirstPage = () => setCurrentPage(1);

  // Função para ir para a última página
  const goToLastPage = () => setCurrentPage(totalPages);

  // Função para ir para a próxima página
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Função para ir para a página anterior
  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Função para alterar a quantidade de itens por página
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
  };

  // Verificar se um veículo está com prazo vencido
  const isPrazoVencido = (prazo) => {
    const prazoDate = new Date(prazo);
    const today = new Date();
    return prazoDate < today;
  };

  // Função para expandir/colapsar linha
  const toggleRowExpansion = (id) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(id)) {
      newExpandedRows.delete(id);
    } else {
      newExpandedRows.add(id);
    }
    setExpandedRows(newExpandedRows);
  };

  // Função para formatar valor monetário
  const formatCurrency = (value) => {
    const numericValue = value.replace(/\D/g, '');
    const formattedValue = (parseInt(numericValue || '0') / 100).toLocaleString(
      'pt-BR',
      {
        style: 'currency',
        currency: 'BRL',
      }
    );
    return formattedValue;
  };

  function handlePdfUpload(event) {
    const files = Array.from(event.target.files);
    const pdfFiles = files.filter((file) => file.type === 'application/pdf');
    setPdfFiles((prevFiles) => [...prevFiles, ...pdfFiles]);
  }

  function handleSave() {
    const formData = new FormData();

    pdfFiles.forEach((file, index) => {
      formData.append(`pdf_${index}`, file);
    });
  }

  return (
    <div className="p-6 rounded-md shadow bg-gray-300 mx-2">
      <h1 className="text-2xl font-semibold mb-6">Veículos em Manutenção</h1>

      {/* Filtro e controles */}
      <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="relative w-full md:w-64">
          <input
            type="text"
            placeholder="Buscar por placa, usuário..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full border border-gray-300 rounded-md pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
          />
          <MagnifyingGlass
            size={20}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
          />
        </div>

        <div className="flex items-center gap-2 w-full md:w-auto justify-between md:justify-end">
          <span className="text-sm text-gray-200">Itens por página:</span>
          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>

      {/* Tabela de manutenções */}
      <div className="overflow-x-auto bg-gray-300 rounded-md">
        <table className="min-w-full divide-y divide-gray-400">
          <thead className="bg-gray-350">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('placa')}
              >
                <div className="flex items-center">Placa {renderSortIcon('placa')}</div>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('valor')}
              >
                <div className="flex items-center">Valor {renderSortIcon('valor')}</div>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('data_entrada')}
              >
                <div className="flex items-center">
                  Data de Entrada {renderSortIcon('data_entrada')}
                </div>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('prazo')}
              >
                <div className="flex items-center">Prazo {renderSortIcon('prazo')}</div>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider"
              >
                Motivo
              </th>

              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider"
              >
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-gray-900">
                  <div className="flex justify-center items-center">
                    <svg
                      className="animate-spin h-5 w-5 text-blue-600 mr-3"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Carregando manutenções...
                  </div>
                </td>
              </tr>
            ) : currentItems.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                  Nenhuma manutenção encontrada.
                </td>
              </tr>
            ) : (
              currentItems.map((maintenance) => (
                <>
                  <tr key={maintenance.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                      <div className="font-medium">{maintenance.placa}</div>
                      <div className="text-xs text-gray-500">
                        Registrado por: {maintenance.usuario}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                      {maintenance.valor !== null && maintenance.valor !== undefined
                        ? maintenance.valor.toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          })
                        : 'R$ 0,00'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                      {formatDateTime(maintenance.data_entrada)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                      {formatDate(maintenance.prazo)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          isPrazoVencido(maintenance.prazo)
                            ? 'bg-red-100 text-red-800'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {isPrazoVencido(maintenance.prazo)
                          ? 'Prazo vencido'
                          : 'Em manutenção'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          className="p-1 transition-colors"
                          title={
                            expandedRows.has(maintenance.id) ? 'Recolher' : 'Expandir'
                          }
                          onClick={() => toggleRowExpansion(maintenance.id)}
                        >
                          {expandedRows.has(maintenance.id) ? (
                            <Minus
                              size={20}
                              className="text-blue-500 hover:text-blue-700"
                              weight="bold"
                            />
                          ) : (
                            <Plus
                              size={20}
                              className="text-blue-500 hover:text-blue-700"
                              weight="bold"
                            />
                          )}
                        </button>
                        <button
                          className="p-1 transition-colors"
                          title="Remover"
                          onClick={() => handleRemoveMaintenance(maintenance.id)}
                        >
                          <X
                            size={20}
                            className="text-red-300 hover:text-red-600"
                            weight="bold"
                          />
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* Linha expandida com formulário */}
                  {expandedRows.has(maintenance.id) && (
                    <tr className="bg-gray-50">
                      <td colSpan={6} className="px-6 py-4">
                        <div className="bg-white p-4 rounded-lg shadow-sm border">
                          <h3 className="text-lg font-medium text-gray-900 mb-4">
                            Detalhes da Manutenção - {maintenance.placa}
                          </h3>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Campo de Observações */}
                            <div className="md:col-span-2">
                              <label
                                htmlFor={`observacoes-${maintenance.id}`}
                                className="block text-sm font-medium text-gray-700 mb-2"
                              >
                                Observações
                              </label>
                              <textarea
                                id={`observacoes-${maintenance.id}`}
                                rows={4}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                                placeholder="Digite as observações sobre a manutenção..."
                              />
                            </div>

                            {/* Campo de Valor do Serviço */}
                            <div>
                              <label
                                htmlFor={`valor-servico-${maintenance.id}`}
                                className="block text-sm font-medium text-gray-700 mb-2"
                              >
                                Valor do Serviço
                              </label>
                              <input
                                type="text"
                                id={`valor-servico-${maintenance.id}`}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                                placeholder="R$ 0,00"
                                onChange={(e) => {
                                  const formatted = formatCurrency(e.target.value);
                                  e.target.value = formatted;
                                }}
                              />
                            </div>

                            {/* Campo de Upload de PDF */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Anexar Arquivos PDF
                              </label>
                              <div className="flex items-center">
                                <input
                                  type="file"
                                  id={`pdf-upload-${maintenance.id}`}
                                  accept=".pdf"
                                  multiple
                                  className="hidden"
                                  onChange={handlePdfUpload}
                                />
                                <label
                                  htmlFor={`pdf-upload-${maintenance.id}`}
                                  className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 cursor-pointer transition-colors"
                                >
                                  <PaperclipHorizontal size={20} className="mr-2" />
                                  Selecionar PDFs
                                </label>
                              </div>
                            </div>
                          </div>

                          {/* Botões de ação */}
                          <div className="flex justify-end gap-2 mt-4">
                            <button
                              onClick={() => toggleRowExpansion(maintenance.id)}
                              className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                            >
                              Cancelar
                            </button>
                            <button
                              onClick={() => {
                                handleSave();
                                toast.success('Dados salvos com sucesso!');
                              }}
                              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
                            >
                              Salvar
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Paginação */}
      <div className="py-3 flex items-center justify-between border-t border-gray-400 mt-4">
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-200">
              Mostrando <span className="font-medium">{indexOfFirstItem + 1}</span> a{' '}
              <span className="font-medium">
                {Math.min(indexOfLastItem, filteredMaintenances.length)}
              </span>{' '}
              de <span className="font-medium">{filteredMaintenances.length}</span>{' '}
              resultados
            </p>
          </div>
          <div>
            <nav
              className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
              aria-label="Pagination"
            >
              <button
                onClick={goToFirstPage}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                  currentPage === 1
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                <span className="sr-only">Primeira página</span>
                <CaretDoubleLeft size={18} />
              </button>
              <button
                onClick={prevPage}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
                  currentPage === 1
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                <span className="sr-only">Anterior</span>
                <CaretLeft size={18} />
              </button>

              {/* Números de página */}
              {Array.from({ length: Math.min(5, totalPages) }).map((_, i) => {
                // Mostrar páginas ao redor da página atual
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={i}
                    onClick={() => paginate(pageNum)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      currentPage === pageNum
                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
                  currentPage === totalPages
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                <span className="sr-only">Próxima</span>
                <CaretRight size={18} />
              </button>
              <button
                onClick={goToLastPage}
                disabled={currentPage === totalPages}
                className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                  currentPage === totalPages
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                <span className="sr-only">Última página</span>
                <CaretDoubleRight size={18} />
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}
